import React, { useState, useEffect, useCallback } from 'react';
import { X, ChevronRight, ChevronLeft, AlertTriangle, RotateCcw } from 'lucide-react';
import { Button } from './ui/button';

/**
 * AI Readiness Assessment Modal
 * A 5-question assessment to evaluate AI readiness
 * Results in CTA to book a meeting with AI Architects
 */

const CALENDLY_URL = "https://calendly.com/chengappa-nivadigital/30min";

// Assessment questions based on the spec (condensed to 5 questions)
const QUESTIONS = [
  {
    id: 1,
    dimension: "Strategy & Leadership",
    question: "Does your organisation have a defined AI strategy or roadmap?",
    options: [
      { text: "No strategy — we haven't discussed AI formally", score: 1 },
      { text: "We're aware of AI but have no formal plan", score: 3 },
      { text: "Informal exploration, no documented roadmap", score: 5 },
      { text: "Draft strategy, some pilot projects underway", score: 7 },
      { text: "Clear AI strategy with executive sponsorship and KPIs", score: 10 }
    ]
  },
  {
    id: 2,
    dimension: "Data Readiness",
    question: "What is the current state of your data infrastructure?",
    options: [
      { text: "Data is scattered across systems with no governance", score: 1 },
      { text: "Some data is collected but not centralised", score: 3 },
      { text: "Data exists but quality is inconsistent, limited governance", score: 5 },
      { text: "Centralised data with reasonable quality and basic governance", score: 7 },
      { text: "Robust data platform with strong governance and high-quality data", score: 10 }
    ]
  },
  {
    id: 3,
    dimension: "People & Culture",
    question: "How well does your team understand AI and its capabilities?",
    options: [
      { text: "No awareness — most staff don't know what AI is", score: 1 },
      { text: "Heard about AI, very basic awareness", score: 3 },
      { text: "Moderate understanding, some experimentation with tools like ChatGPT", score: 5 },
      { text: "Good understanding, using AI in several workflows", score: 7 },
      { text: "Deep expertise, AI embedded as a default working tool", score: 10 }
    ]
  },
  {
    id: 4,
    dimension: "Governance & Risk",
    question: "Do you have AI governance, ethics, or compliance policies?",
    options: [
      { text: "No policies, no consideration of AI risks", score: 1 },
      { text: "Aware of risks but no formal policies", score: 3 },
      { text: "Draft guidelines exist but are not enforced", score: 5 },
      { text: "Policies exist and are partially implemented", score: 7 },
      { text: "Complete governance framework, actively monitored and audited", score: 10 }
    ]
  },
  {
    id: 5,
    dimension: "AI Adoption Intent",
    question: "How does your organisation approach risk when adopting new technology?",
    options: [
      { text: "Very risk-averse — avoid new tech until proven safe", score: 1 },
      { text: "Cautious — adopt slowly with heavy approval processes", score: 3 },
      { text: "Balanced — assess risk case-by-case", score: 5 },
      { text: "Progressive — lean toward adoption with managed risk", score: 7 },
      { text: "Innovative — move fast, iterate, accept calculated risk", score: 10 }
    ]
  }
];

// Maturity tiers based on overall score
const MATURITY_TIERS = [
  { min: 1, max: 2, level: "🌱 AI Beginner", description: "You're at the starting line of your AI journey. There's significant opportunity to build foundational capabilities.", color: "#ef4444" },
  { min: 3, max: 4, level: "🌿 AI Explorer", description: "You're aware of AI's potential and beginning to explore. Focus on building data infrastructure and leadership alignment.", color: "#f97316" },
  { min: 5, max: 6, level: "🚀 AI Adopter", description: "You're actively adopting AI with some success. Now is the time to scale strategically and build governance frameworks.", color: "#eab308" },
  { min: 7, max: 8, level: "🏆 AI Leader", description: "You have mature AI capabilities and are scaling effectively. Focus on optimization and maintaining competitive advantage.", color: "#22c55e" },
  { min: 9, max: 10, level: "🔬 AI Innovator", description: "You're at the frontier of AI adoption. Continue pushing boundaries while ensuring sustainable practices.", color: "#06b6d4" }
];

const AIReadinessAssessment = ({ isOpen, onClose }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState(0);

  const loadingMessages = [
    "Scoring your AI maturity across 4 dimensions…",
    "Benchmarking against industry standards…",
    "Identifying your highest-impact next steps…",
    "Generating your personalised AI roadmap…"
  ];

  // Rotate loading messages
  useEffect(() => {
    if (isLoading) {
      const interval = setInterval(() => {
        setLoadingMessage(prev => (prev + 1) % loadingMessages.length);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [isLoading, loadingMessages.length]);

  // Calculate results
  const calculateResults = useCallback(() => {
    const scores = Object.values(answers);
    const totalScore = scores.reduce((acc, score) => acc + score, 0);
    const averageScore = Math.round(totalScore / QUESTIONS.length);
    
    // Find maturity tier
    const tier = MATURITY_TIERS.find(t => averageScore >= t.min && averageScore <= t.max) || MATURITY_TIERS[0];
    
    // Calculate dimension scores
    const dimensionScores = QUESTIONS.map((q, idx) => ({
      dimension: q.dimension,
      score: answers[idx] || 0
    }));

    // Find weakest dimension
    const weakest = dimensionScores.reduce((min, d) => d.score < min.score ? d : min, dimensionScores[0]);

    return {
      overallScore: averageScore,
      tier,
      dimensionScores,
      weakestDimension: weakest.dimension
    };
  }, [answers]);

  const handleSelectAnswer = (questionIndex, score) => {
    setAnswers(prev => ({ ...prev, [questionIndex]: score }));
  };

  const handleNext = () => {
    if (currentQuestion < QUESTIONS.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      // Show loading then results
      setIsLoading(true);
      setTimeout(() => {
        setIsLoading(false);
        setShowResults(true);
      }, 4000);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleRetake = () => {
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setIsLoading(false);
  };

  const handleClose = () => {
    handleRetake();
    onClose();
  };

  if (!isOpen) return null;

  const progress = ((currentQuestion + 1) / QUESTIONS.length) * 100;
  const currentQ = QUESTIONS[currentQuestion];
  const selectedAnswer = answers[currentQuestion];
  const results = showResults ? calculateResults() : null;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9998]"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center overflow-hidden">
                <img src="/niva-logo.jpg" alt="NIVA" className="w-8 h-8 object-contain" />
              </div>
              <div>
                <h2 className="text-white font-semibold text-lg">AI Readiness Assessment</h2>
                <p className="text-gray-400 text-sm">Free · 2 Minutes · Instant Results</p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
            
            {/* Loading State */}
            {isLoading && (
              <div className="py-16 text-center">
                <div className="relative w-24 h-24 mx-auto mb-6">
                  {/* Track circle */}
                  <svg className="absolute inset-0 w-full h-full" viewBox="0 0 72 72">
                    <circle cx="36" cy="36" r="30" fill="none" stroke="#EEF0F5" strokeWidth="5" />
                  </svg>
                  {/* Spinning circle */}
                  <svg className="absolute inset-0 w-full h-full animate-spin" viewBox="0 0 72 72" style={{ animationDuration: '1.5s' }}>
                    <circle 
                      cx="36" cy="36" r="30" fill="none" 
                      stroke="#dc2626" strokeWidth="5" 
                      strokeDasharray="50 138" 
                      strokeLinecap="round"
                    />
                  </svg>
                  {/* Center icon */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <img src="/niva-logo.jpg" alt="NIVA" className="w-8 h-8 rounded" />
                  </div>
                </div>
                <p className="text-xl font-semibold text-gray-900 mb-2">Analysing your responses…</p>
                <p className="text-gray-500">{loadingMessages[loadingMessage]}</p>
              </div>
            )}

            {/* Results State */}
            {showResults && results && (
              <div className="space-y-6">
                {/* Score Header */}
                <div className="flex items-center gap-6 p-6 bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl">
                  {/* Score Ring */}
                  <div className="relative w-28 h-28 flex-shrink-0">
                    <svg viewBox="0 0 90 90" className="w-full h-full">
                      <circle cx="45" cy="45" r="38" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="6" />
                      <circle 
                        cx="45" cy="45" r="38" fill="none" 
                        stroke={results.tier.color}
                        strokeWidth="6" 
                        strokeDasharray={`${(results.overallScore / 10) * 239} 239`}
                        strokeLinecap="round"
                        transform="rotate(-90 45 45)"
                        style={{ transition: 'stroke-dasharray 0.8s ease' }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex flex-col items-center justify-center">
                      <span className="text-3xl font-bold text-white">{results.overallScore}</span>
                      <span className="text-gray-400 text-sm">/ 10</span>
                    </div>
                  </div>
                  {/* Meta */}
                  <div className="flex-1">
                    <div className="text-2xl font-bold text-white mb-2">{results.tier.level}</div>
                    <p className="text-gray-300 text-sm">{results.tier.description}</p>
                  </div>
                </div>

                {/* Priority Gap */}
                <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
                  <span className="text-amber-800">
                    <strong>Priority focus area:</strong> {results.weakestDimension}
                  </span>
                </div>

                {/* Dimension Scores */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-gray-900">Your Dimension Scores</h3>
                  {results.dimensionScores.map((dim, idx) => (
                    <div key={idx} className="flex items-center gap-4">
                      <span className="text-sm text-gray-600 w-40">{dim.dimension}</span>
                      <div className="flex-1 h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all duration-500"
                          style={{ 
                            width: `${(dim.score / 10) * 100}%`,
                            backgroundColor: dim.score <= 3 ? '#ef4444' : dim.score <= 6 ? '#eab308' : '#22c55e'
                          }}
                        />
                      </div>
                      <span className="text-sm font-semibold text-gray-900 w-8">{dim.score}</span>
                    </div>
                  ))}
                </div>

                {/* CTA */}
                <div className="p-6 bg-gradient-to-r from-red-50 to-orange-50 border border-red-100 rounded-xl text-center">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Ready to accelerate your AI journey?
                  </h3>
                  <p className="text-gray-600 mb-4">
                    Book a <strong>free 30-minute strategy call</strong> with one of our AI Architects to discuss your results and next steps.
                  </p>
                  <a 
                    href={CALENDLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 text-lg">
                      Book a Free Strategy Call
                      <ChevronRight className="ml-2 w-5 h-5" />
                    </Button>
                  </a>
                </div>

                {/* Retake */}
                <button 
                  onClick={handleRetake}
                  className="w-full flex items-center justify-center gap-2 text-gray-500 hover:text-gray-700 transition-colors py-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  <span>Retake Assessment</span>
                </button>
              </div>
            )}

            {/* Quiz State */}
            {!isLoading && !showResults && (
              <>
                {/* Progress */}
                <div className="mb-6">
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
                    <div 
                      className="h-full bg-red-600 rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-red-600 font-medium">{currentQ.dimension}</span>
                    <span className="text-gray-500">Question {currentQuestion + 1} of {QUESTIONS.length}</span>
                  </div>
                </div>

                {/* Question */}
                <div className="mb-6">
                  <p className="text-xl font-semibold text-gray-900 mb-6">{currentQ.question}</p>
                  
                  {/* Options */}
                  <div className="space-y-3">
                    {currentQ.options.map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => handleSelectAnswer(currentQuestion, option.score)}
                        className={`w-full p-4 rounded-xl border-2 text-left transition-all duration-200 flex items-center gap-4 ${
                          selectedAnswer === option.score
                            ? 'border-red-600 bg-red-50'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          selectedAnswer === option.score
                            ? 'border-red-600 bg-red-600'
                            : 'border-gray-300'
                        }`}>
                          {selectedAnswer === option.score && (
                            <div className="w-2 h-2 bg-white rounded-full" />
                          )}
                        </div>
                        <span className={`${
                          selectedAnswer === option.score ? 'text-gray-900 font-medium' : 'text-gray-700'
                        }`}>
                          {option.text}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex justify-between pt-4 border-t border-gray-100">
                  <Button
                    variant="ghost"
                    onClick={handleBack}
                    disabled={currentQuestion === 0}
                    className={currentQuestion === 0 ? 'invisible' : ''}
                  >
                    <ChevronLeft className="w-4 h-4 mr-2" />
                    Back
                  </Button>
                  <Button
                    onClick={handleNext}
                    disabled={selectedAnswer === undefined}
                    className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {currentQuestion === QUESTIONS.length - 1 ? 'Get My AI Report' : 'Next'}
                    <ChevronRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default AIReadinessAssessment;
